import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import { ApiError } from "./api";

/**
 * A list, plus the writes that change it, for one resource.
 *
 * The `fields` map returned here carries per-field messages straight from the
 * API's 422 response, so a form shows "Enter an amount like 1500.00" against
 * the input that caused it rather than a banner the user has to decode.
 *
 * Create and update share that error state deliberately: one form does both
 * jobs, so it has one place to look for what went wrong, and switching between
 * adding and editing cannot leave the other one's message on screen.
 */
export function useResource<TList, TCreate, TUpdate = Partial<TCreate>>(opts: {
  key: readonly unknown[];
  list: () => Promise<TList>;
  create: (body: TCreate) => Promise<unknown>;
  /** Save an edit to a row that already exists. Omit for create-only lists. */
  update?: ((id: string, body: TUpdate) => Promise<unknown>) | undefined;
  /** Query keys to refresh after a write — e.g. projects after a new client. */
  alsoInvalidate?: readonly (readonly unknown[])[];
  /** False holds the list back — for a tab whose page isn't on screen yet. */
  enabled?: boolean | undefined;
}) {
  const queryClient = useQueryClient();
  const [formError, setFormError] = useState<string | null>(null);
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});

  const query = useQuery({
    queryKey: opts.key,
    queryFn: opts.list,
    enabled: opts.enabled ?? true,
  });

  const shared = {
    onMutate: () => {
      setFormError(null);
      setFieldErrors({});
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: opts.key });
      for (const k of opts.alsoInvalidate ?? []) {
        await queryClient.invalidateQueries({ queryKey: k });
      }
    },
    onError: (err: unknown) => {
      if (err instanceof ApiError) {
        setFormError(err.fields ? null : err.message);
        setFieldErrors(err.fields ?? {});
        return;
      }
      setFormError("Couldn't save. Check your connection and try again.");
    },
  };

  const create = useMutation({ mutationFn: opts.create, ...shared });

  const update = useMutation({
    mutationFn: async ({ id, body }: { id: string; body: TUpdate }) => {
      if (!opts.update) throw new Error("This resource has no update");
      return opts.update(id, body);
    },
    ...shared,
  });

  return {
    query,
    create: create.mutateAsync,
    update: update.mutateAsync,
    saving: create.isPending || update.isPending,
    formError,
    fieldErrors,
    clearErrors: () => {
      setFormError(null);
      setFieldErrors({});
    },
  };
}
